import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Grid3X3, List, Globe, Clock } from 'lucide-react';
import { getWeekStart } from '../../utils/calendarHelpers';

/**
 * Get the current time in a specific timezone
 */
const getTimeInTimezone = (timezone) => {
  try {
    const now = new Date();
    return now.toLocaleString('en-US', { 
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (e) {
    return new Date().toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
};

/**
 * Get timezone abbreviation (e.g., "HKT", "EST", "PST")
 */
const getTimezoneAbbr = (timezone) => {
  try {
    const now = new Date();
    const options = { timeZone: timezone, timeZoneName: 'short' };
    const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(now);
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : timezone;
  } catch (e) {
    return timezone;
  }
};

/**
 * CalendarHeader - Navigation and view toggle controls
 */
const CalendarHeader = ({
  calendar,
  viewMode,
  setViewMode,
  currentDate,
  setCurrentDate,
  currentWeekStart,
  setCurrentWeekStart
  // onSyncCalendar removed (Jan 15, 2026) - moved to Profile Settings
}) => {
  const [currentTimeDisplay, setCurrentTimeDisplay] = useState('');
  const calendarTimezone = calendar?.timezone || 'UTC';
  const timezoneAbbr = getTimezoneAbbr(calendarTimezone);

  // Update current time display every minute
  useEffect(() => {
    const updateTime = () => {
      setCurrentTimeDisplay(getTimeInTimezone(calendarTimezone));
    };
    
    updateTime();
    const timer = setInterval(updateTime, 60000);
    return () => clearInterval(timer);
  }, [calendarTimezone]);
  const handlePrevWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const handlePrevMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handlePrevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
    setCurrentWeekStart(getWeekStart(newDate));
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
    setCurrentWeekStart(getWeekStart(newDate));
  };

  const handleTodayClick = () => {
    const today = new Date();
    setCurrentDate(today);
    setCurrentWeekStart(getWeekStart(today));
  };

  const handlePrev = viewMode === 'month' ? handlePrevMonth : viewMode === 'day' ? handlePrevDay : handlePrevWeek;
  const handleNext = viewMode === 'month' ? handleNextMonth : viewMode === 'day' ? handleNextDay : handleNextWeek;

  const getDateDisplay = () => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewMode === 'day') {
      return currentDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } else {
      return currentWeekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">{calendar.name}</h1>
          <p className="text-gray-600 mt-1">
            {calendar.type} • {calendar.location} • {calendar.office_hours}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('month')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'month' 
                  ? 'bg-white text-gray-800 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <CalendarDays className="w-4 h-4" />
              Month
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'week' 
                  ? 'bg-white text-gray-800 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
              Week
            </button>
            <button
              onClick={() => setViewMode('day')}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'day' 
                  ? 'bg-white text-gray-800 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <List className="w-4 h-4" />
              Day
            </button>
          </div>

          {/* Today Button */}
          <button
            onClick={handleTodayClick}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            Today
          </button>

          {/* Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrev}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-lg font-semibold min-w-[200px] text-center">
              {getDateDisplay()}
            </span>
            <button
              onClick={handleNext}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* SYNC BUTTON REMOVED (Jan 15, 2026):
              Calendar sync moved to Profile Settings page for better organization.
              Customers and calendar admins can access it from Profile Settings > Calendar Integration
          */}
        </div>
      </div>
      
      {/* Timezone Indicator */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Globe className="w-4 h-4 text-blue-500" />
            <span>All times shown in</span>
            <span className="font-semibold text-gray-800 bg-blue-50 px-2 py-0.5 rounded">
              {calendarTimezone} ({timezoneAbbr})
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-green-500" />
            <span className="text-gray-600">Current time:</span>
            <span className="font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
              {currentTimeDisplay}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarHeader;
